import { useState, useEffect, useCallback } from 'react';
import { useTypedSelector } from '../../hooks';
import { Lock } from '../../devices/locker/interface/Locker/Lock';
import './styles/DebugPanelStyle.css';

const DebugPanel = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [sidInput, setSidInput] = useState('1');
  const [cidInput, setCidInput] = useState('1');
  const [lastAction, setLastAction] = useState<string>('');

  const locker = useTypedSelector(({ devices }) => devices.locker);

  useEffect(() => {
    if (locker) {
      setIsConnected(locker.isConnected());

      const onConnected = () => {
        setIsConnected(true);
        setLastAction('Connected');
      };
      const onDisconnected = () => {
        setIsConnected(false);
        setLastAction('Disconnected');
      };

      locker.addEventListener('connection', onConnected);
      locker.addEventListener('disconnection', onDisconnected);

      return () => {
        locker.removeEventListener('connection', onConnected);
        locker.removeEventListener('disconnection', onDisconnected);
      };
    }
  }, [locker]);

  const handleReconnect = useCallback(() => {
    if (locker) {
      setLastAction('Reconnecting...');
      locker.reconnect();
    } else {
      setLastAction('No locker instance');
    }
  }, [locker]);

  const handleTestOpen = useCallback(() => {
    if (locker) {
      const testLock = new Lock(sidInput, cidInput);
      setLastAction(`Opening lock sid:${sidInput} cid:${cidInput}...`);
      locker.openLockAsync(testLock).then(() => {
        setLastAction(`Sent open command for sid:${sidInput} cid:${cidInput}`);
      }).catch((err) => {
        setLastAction(`Error: ${err}`);
      });
    } else {
      setLastAction('No locker instance');
    }
  }, [locker, sidInput, cidInput]);

  const handleGetMap = useCallback(() => {
    if (locker) {
      setLastAction('Getting locker map...');
      locker.getLockerMapAsync().then(() => {
        setLastAction('Sent get locker map command');
      }).catch((err) => {
        setLastAction(`Error: ${err}`);
      });
    } else {
      setLastAction('No locker instance');
    }
  }, [locker]);

  return (
    <div className="debug-panel">
      <button
        className="debug-panel-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        🔧 {isConnected ? '🟢' : '🔴'}
      </button>

      {isExpanded && (
        <div className="debug-panel-content">
          <div className="debug-panel-header">
            <strong>Locker Debug Panel</strong>
            <span className={`debug-status ${isConnected ? 'connected' : 'disconnected'}`}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </span>
          </div>

          <div className="debug-panel-actions">
            <button onClick={handleReconnect} className="debug-btn">
              🔄 Reconnect WS
            </button>

            <button onClick={handleGetMap} className="debug-btn">
              🗺️ Get Locker Map
            </button>
          </div>

          <div className="debug-panel-section">
            <strong>Test Open Lock:</strong>
            <div className="debug-inputs">
              <label>
                SID:
                <input
                  type="text"
                  value={sidInput}
                  onChange={(e) => setSidInput(e.target.value)}
                  className="debug-input"
                />
              </label>
              <label>
                CID:
                <input
                  type="text"
                  value={cidInput}
                  onChange={(e) => setCidInput(e.target.value)}
                  className="debug-input"
                />
              </label>
            </div>
            <button onClick={handleTestOpen} className="debug-btn debug-btn-primary">
              🔓 Open Lock
            </button>
          </div>

          {lastAction && (
            <div className="debug-panel-log">
              <small>Last: {lastAction}</small>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { DebugPanel };
